import { type ModelUpdatedEvent, type RowDragEvent, type RowNode } from "ag-grid-community";
import { differenceBy } from "lodash-es";
import { createRef, PureComponent, type ReactElement } from "react";

import { ATTRIBUTE_TYPE } from "@/settings/settings.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

import { isAutomaticRepresenterAttribute } from "../../app-utils/sample.js";
import { filterTemplateSuggestions } from "../../app-utils/sample-template.js";
import { type ISelectionModel, type TGridSelModelIdentifier } from "../../app-utils/selection-model.js";
import { type Attribute, type RawAttributeDefault } from "../../graphql.js";
import { type IIdentifiable, type ITableColumn } from "../../redux/modules/table-data.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import { type IAttributeWithDefault } from "./AddSampleTypeDialog.js";
import { default as ConfigureSampleTypeAttributeDialog } from "./ConfigureSampleTypeAttributeDialog.js";
import { SelectAttributesManagerControls } from "./SelectAttributeManagerControls.js";

export interface IAttachAttributesManagerProps {
  columnDefs: ITableColumn[];
  assignedAttributes: IAttributeWithDefault[];
  availableAttributes: IAttributeWithDefault[]; // all att filtered by COS, including the already assigned
  isLoadingAvailableAttributes: boolean;
  parentContext?: boolean;
  attributesForDecoration: Attribute[];

  handleAttributesChanged: (assignedAttributes: IAttributeWithDefault[]) => void;
  refreshAvailableAttributes: () => Promise<IAttributeWithDefault[]>;
  getRemoveAttributesDisabledTooltip?: (selectedAttributeIds: number[]) => string;
}

interface IAttachAttributesManagerState {
  showAddPopover: boolean;
  showConfigurePopover: boolean;
  itemsSelectedCount: number;
}

class AttachAttributesManager extends PureComponent<IAttachAttributesManagerProps, IAttachAttributesManagerState> {
  private tableInstance = createRef<InMemoryTableWithSelectionModel>();
  private attributeToConfigure: IAttributeWithDefault = null;

  constructor(props) {
    super(props);

    this.state = {
      showAddPopover: false,
      showConfigurePopover: false,
      itemsSelectedCount: 0,
    };
  }

  onConfigureSampleTypeAttribute = (attribute: IAttributeWithDefault) => {
    this.attributeToConfigure = attribute;
    this.setState({ showConfigurePopover: true });
  };

  createRowData = (attributes: IAttributeWithDefault[]): IIdentifiable[] => {
    return attributes.map((attr: IAttributeWithDefault) => {
      const disabledConfiguration: boolean =
        isAutomaticRepresenterAttribute(attr) ||
        (attr.attributeType.id !== ATTRIBUTE_TYPE.FREE_TEXT &&
          attr.attributeType.id !== ATTRIBUTE_TYPE.FREE_NUMERICAL &&
          attr.attributeType.id !== ATTRIBUTE_TYPE.CONTROLLED_TEXT);
      return {
        ...attr,
        configure: {
          disabled: disabledConfiguration,
          tooltip: disabledConfiguration ? "You cannot configure this attribute" : "Configure this attribute",
          onClick: disabledConfiguration ? undefined : () => this.onConfigureSampleTypeAttribute(attr),
        },
        attributeDefault: { ...attr.attributeDefault }, // spread operator to force the value to change so that the cell updates when the decoration attributes have been fetched
        editAction: attr.attributeDefault ? { ...attr.attributeDefault } : true, // Required value for the pseudo column so that the CellDataChecker (DEV only) does not complain ; using attributeDefault to force the cell to refresh and reflect the new data in the associated dialog
      };
    });
  };

  addAttributes = (newAttributes: Attribute[]) => {
    const controlledValues = this.props.assignedAttributes.concat(newAttributes);
    this.props.handleAttributesChanged(controlledValues);
  };

  handleRemoveSelection = () => {
    const selectedIds: TGridSelModelIdentifier[] = this.tableInstance.current.getSelectedIds();
    const attributes: Attribute[] = this.props.assignedAttributes.filter(
      (attribute: Attribute) => !selectedIds.includes(attribute.id),
    );

    this.tableInstance.current.resetSelection();
    this.props.handleAttributesChanged(attributes);
  };

  selectionModelUpdated = (selectionModel: ISelectionModel) => {
    this.setState({ itemsSelectedCount: selectionModel.selectedIds.length });
  };

  rowsUpdated = (modelUpdated: ModelUpdatedEvent | RowDragEvent) => {
    const attributes = [];
    modelUpdated.api.forEachNode((rowNode: RowNode) => {
      // we do not take the row data, since this one has different data (different defaults structure, configuration data...)
      attributes.push(this.props.assignedAttributes.find((att: Attribute) => rowNode.data.id === att.id));
    });
    this.props.handleAttributesChanged(attributes);
  };

  getAddAttributesDisabledTooltip = () => {
    if (this.props.isLoadingAvailableAttributes) {
      return "Please wait...";
    }
  };

  getRemoveAttributesDisabledTooltip = () => {
    let tooltip: string;
    const selectedIds: number[] =
      (this.tableInstance.current && this.tableInstance.current.getSelectedIds().map((v) => Number(v))) || [];
    tooltip = this.props.getRemoveAttributesDisabledTooltip?.(selectedIds);
    if (!tooltip) {
      if (selectedIds.length === 0) {
        return "No selection done";
      }
    }
    return tooltip;
  };

  render(): ReactElement {
    return (
      <FormInputGroup>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <FormLabel>
            Attributes <span className="count">({this.props.assignedAttributes.length})</span>
          </FormLabel>
          <SelectAttributesManagerControls
            availableAttributes={differenceBy(this.props.availableAttributes, this.props.assignedAttributes, "id")}
            onAddAttributes={this.addAttributes}
            handleRemoveSelection={this.handleRemoveSelection}
            getRemoveAttributesDisabledTooltip={this.getRemoveAttributesDisabledTooltip}
            getAddAttributesDisabledTooltip={this.getAddAttributesDisabledTooltip}
            refreshAvailableAttributes={this.props.refreshAvailableAttributes}
            parentContext={this.props.parentContext}
          />
        </div>

        <InMemoryTableWithSelectionModel
          columns={this.props.columnDefs}
          ref={this.tableInstance}
          rowGridData={this.createRowData(this.props.assignedAttributes)}
          uniqueGridIdentifier={"sample-type-attributes"}
          useSelectionModel={true}
          allowRowDragging={true}
          noRowsMessage={"Add some attributes"}
          selectionModelUpdated={this.selectionModelUpdated} // force the component to update the controls
          gridProps={{
            onRowDragEnd: this.rowsUpdated,
            domLayout: "autoHeight",
          }}
          gridContext={{ attributesForDecoration: this.props.attributesForDecoration }}
          isLoading={this.props.isLoadingAvailableAttributes}
        />

        {this.state.showConfigurePopover && (
          <ConfigureSampleTypeAttributeDialog
            attribute={this.attributeToConfigure}
            onCancel={() => this.setState({ showConfigurePopover: false })}
            onApply={this.applyConfiguration}
            templateAttributes={filterTemplateSuggestions(this.props.assignedAttributes, this.attributeToConfigure)}
          />
        )}
      </FormInputGroup>
    );
  }

  applyConfiguration = (attributeId: number, defaultOrTemplate: RawAttributeDefault) => {
    this.setState({ showConfigurePopover: false });

    const attributes: IAttributeWithDefault[] = this.props.assignedAttributes.map(
      (attribute: IAttributeWithDefault) => {
        if (attribute.id === attributeId) {
          return {
            ...attribute,
            attributeDefault: defaultOrTemplate,
          };
        }
        return attribute;
      },
    );
    this.props.handleAttributesChanged(attributes);
  };
}

export default AttachAttributesManager;
